/**
 * ModalityVerdict — shared helpers that turn a ModalitySemanticDetector
 * verdict into a neutral, observation-only human-readable label, plus the
 * body-lean classifier. Used by both the live demo and the scene-log app
 * so the two surfaces stay consistent (single source of truth for wording
 * and for the mirror-independent lean sign).
 *
 * Wording is description-only: states what was observed (direction,
 * repetition count, sustained seconds) and never asserts intent.
 */
import { ZONE_META, type GazeZone } from './GazeZoneClassifier';
import type { SemanticVerdict } from './ModalitySemanticDetector';

/** Shoulder-line deviation (deg) beyond which the torso is leaning. */
export const BODY_LEAN_DEG = 12;

const sec = (ms: number) => (ms / 1000).toFixed(1);

/**
 * Classify torso lean from the scalar shoulderAngle = atan2(y12−y11,
 * x12−x11), where lm11/lm12 are MediaPipe's anatomical LEFT/RIGHT
 * shoulders. Magnitude uses the deviation from horizontal,
 * `min(|θ|, 180−|θ|)`, which is 0° at rest whether or not the image is
 * mirrored. Direction = which shoulder sits lower = sign(sin θ), which
 * depends only on the y-axis and is therefore independent of horizontal
 * mirroring; the result is in the subject's anatomical frame (right
 * shoulder lower ⇒ leaning to the subject's right). `flip` swaps the
 * reported side without a redeploy.
 */
export function classifyBodyLean(
  shoulderAngle: number,
  flip = false,
): 'lean_left' | 'lean_right' | 'upright' {
  const a = Math.abs(shoulderAngle);
  const deviation = Math.min(a, 180 - a);
  if (deviation < BODY_LEAN_DEG) return 'upright';
  const rightShoulderLower = Math.sin((shoulderAngle * Math.PI) / 180) > 0;
  const leanRight = flip ? !rightShoulderLower : rightShoulderLower;
  return leanRight ? 'lean_right' : 'lean_left';
}

/** Gaze-area verdict → "왼쪽 영역 반복 응시 (N회)" / "… 지속 응시 (X.Xs)". */
export function gazeAreaPhrase(v: SemanticVerdict): string {
  const z = (v.state ?? 'forward') as GazeZone;
  const name = ZONE_META[z]?.label ?? String(v.state ?? '');
  return v.kind === 'sustain'
    ? `${name} 지속 응시 (${sec(v.sustainedMs)}s)`
    : `${name} 반복 응시 (${v.count}회)`;
}

/** Hand verdict → "휴대폰 파지 지속 (X.Xs)" / "휴대폰 반복 파지 (N회)". */
export function handPhrase(v: SemanticVerdict): string {
  return v.kind === 'sustain'
    ? `휴대폰 파지 지속 (${sec(v.sustainedMs)}s)`
    : `휴대폰 반복 파지 (${v.count}회)`;
}

/** Body verdict → "상체 좌측 기울임 지속 (X.Xs)" / "상체 좌측 반복 기울임 (N회)". */
export function bodyPhrase(v: SemanticVerdict): string {
  const dir = v.state === 'lean_left' ? '상체 좌측'
    : v.state === 'lean_right' ? '상체 우측' : '상체';
  return v.kind === 'sustain'
    ? `${dir} 기울임 지속 (${sec(v.sustainedMs)}s)`
    : `${dir} 반복 기울임 (${v.count}회)`;
}

/** Salience tier for a verdict (used for HUD colour / event level). */
export function verdictRisk(v: SemanticVerdict): 'low' | 'med' | 'high' {
  if (v.kind === 'sustain') return v.sustainedMs >= 5000 ? 'high' : 'med';
  return v.count >= 4 ? 'high' : 'med';
}
